import { useState, useEffect, memo } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { METRIC_RANGES } from '../../constants/metrics';
import './RatingRow.css';

interface RatingRowProps {
  label: string;
  presentValue: number | null;
  futureValue: number | null;
  onPresentChange: (value: number | null) => void;
  onFutureChange: (value: number | null) => void;
  /**
   * Names the two grade inputs for a screen reader, e.g. "Riseball present grade". The visible
   * name is a sibling <span>, so without this the inputs are anonymous — and a pitcher profile
   * shows up to fourteen of them. Optional so callers that pass neither render exactly as before.
   */
  accessibilityLabelPrefix?: string;
  /** Test hook mirroring the accessible name, e.g. `softballRiseball-present`. */
  testIdPrefix?: string;
}

function RatingRowComponent({
  label,
  presentValue,
  futureValue,
  onPresentChange,
  onFutureChange,
  accessibilityLabelPrefix,
  testIdPrefix,
}: RatingRowProps) {
  const [presentInternal, setPresentInternal] = useState<string>(
    presentValue !== null ? presentValue.toString() : ''
  );
  const [futureInternal, setFutureInternal] = useState<string>(
    futureValue !== null ? futureValue.toString() : ''
  );

  // Debounce the internal values
  const debouncedPresent = useDebounce(presentInternal, 300);
  const debouncedFuture = useDebounce(futureInternal, 300);

  // Update internal values when external values change
  useEffect(() => {
    setPresentInternal(presentValue !== null ? presentValue.toString() : '');
  }, [presentValue]);

  useEffect(() => {
    setFutureInternal(futureValue !== null ? futureValue.toString() : '');
  }, [futureValue]);

  // Call onChange when debounced value changes
  useEffect(() => {
    if (debouncedPresent === '') {
      if (presentValue !== null) {
        onPresentChange(null);
      }
      return;
    }

    const parsed = parseInt(debouncedPresent, 10);
    if (!isNaN(parsed) && parsed >= METRIC_RANGES.rating.min && parsed <= METRIC_RANGES.rating.max) {
      if (parsed !== presentValue) {
        onPresentChange(parsed);
      }
    }
  }, [debouncedPresent, presentValue, onPresentChange]);

  useEffect(() => {
    if (debouncedFuture === '') {
      if (futureValue !== null) {
        onFutureChange(null);
      }
      return;
    }

    const parsed = parseInt(debouncedFuture, 10);
    if (!isNaN(parsed) && parsed >= METRIC_RANGES.rating.min && parsed <= METRIC_RANGES.rating.max) {
      if (parsed !== futureValue) {
        onFutureChange(parsed);
      }
    }
  }, [debouncedFuture, futureValue, onFutureChange]);

  const handlePresentChange = (text: string) => {
    // Only allow digits
    if (text !== '' && !/^\d+$/.test(text)) {
      return;
    }
    setPresentInternal(text);
  };

  const handleFutureChange = (text: string) => {
    // Only allow digits
    if (text !== '' && !/^\d+$/.test(text)) {
      return;
    }
    setFutureInternal(text);
  };

  // Check if values are outside standard 20-80 range (show orange border)
  const isPresentWarning = presentValue !== null && (presentValue < 20 || presentValue > 80);
  const isFutureWarning = futureValue !== null && (futureValue < 20 || futureValue > 80);

  return (
    <div className="rating-row">
      <span className="rating-row__label">{label}</span>
      <div className="rating-row__inputs">
        <input
          type="text"
          inputMode="numeric"
          className={`rating-row__input ${isPresentWarning ? 'rating-row__input--warning' : ''}`}
          value={presentInternal}
          onChange={(e) => handlePresentChange(e.target.value)}
          placeholder="-"
          maxLength={2}
          aria-label={accessibilityLabelPrefix ? `${accessibilityLabelPrefix} present grade` : undefined}
          data-testid={testIdPrefix ? `${testIdPrefix}-present` : undefined}
        />
        <input
          type="text"
          inputMode="numeric"
          className={`rating-row__input ${isFutureWarning ? 'rating-row__input--warning' : ''}`}
          value={futureInternal}
          onChange={(e) => handleFutureChange(e.target.value)}
          placeholder="-"
          maxLength={2}
          aria-label={accessibilityLabelPrefix ? `${accessibilityLabelPrefix} future grade` : undefined}
          data-testid={testIdPrefix ? `${testIdPrefix}-future` : undefined}
        />
      </div>
    </div>
  );
}

export const RatingRow = memo(RatingRowComponent);
